import React, { forwardRef } from "react";
import { ChevronDownOutlined, CheckOutlined } from "@phonepe/enigma-ui-icons";
import { Select as AntdSelect } from "antd";
import { BaseSelectRef } from "rc-select";
import { useEnigmaToken } from "@hooks/useEnigmaToken";
import { Size } from "@type/sizing";
import { HelperText } from "@components/helperText";
import { InputLabel } from "@components/inputLabel";
import { antdStatusMap, helperTextMap } from "@constants/input";
import { StyledSelect, StyledSelectWrapper } from "./select.styles";
import { OptionProps, SelectProps, SelectType } from "./select.types";
import getDropdownList from "./dropdownList";

const DEFAULT_SELECT_CLASSNAME = "enigma-select-default";

const Option: React.FC<OptionProps> = ({ children, ...rest }) => {
    const AntdOption = AntdSelect.Option;
    return <AntdOption {...rest}>{children}</AntdOption>;
};

export const Select = forwardRef<BaseSelectRef, SelectProps>(
    (
        {
            children,
            label,
            required,
            status = "default",
            className = "",
            helperText,
            size = Size.medium,
            menuItemSelectedIcon = <CheckOutlined />,
            listHeight = 320,
            dropdownStyle = { padding: "4px 0" },
            disabled,
            wrapperClassName = "",
            loading = false,
            // Adding the above style inside select class does not reflect in the actual dropdown due to the dropdown being in the different scope.
            ...rest
        },
        ref
    ) => {
        const token = useEnigmaToken();
        return (
            <StyledSelectWrapper className={wrapperClassName}>
                {label && (
                    <InputLabel required={required} disabled={disabled}>
                        {label}
                    </InputLabel>
                )}
                <StyledSelect
                    ref={ref}
                    size={Size[size]}
                    className={`${className} ${DEFAULT_SELECT_CLASSNAME}`}
                    menuItemSelectedIcon={menuItemSelectedIcon}
                    dropdownStyle={dropdownStyle}
                    listHeight={listHeight}
                    suffixIcon={
                        !loading ? (
                            <ChevronDownOutlined
                                size="small"
                                color={
                                    !disabled
                                        ? token.color?.icon?.default
                                        : token.color?.icon?.iconDisabled
                                }
                            />
                        ) : undefined
                    }
                    disabled={disabled}
                    dropdownRender={getDropdownList}
                    status={antdStatusMap[status]}
                    loading={loading}
                    {...rest}
                >
                    {children}
                </StyledSelect>
                {helperText && (
                    <HelperText
                        type={helperTextMap[status] || status}
                        disabled={disabled}
                    >
                        {helperText}
                    </HelperText>
                )}
            </StyledSelectWrapper>
        );
    }
) as SelectType;

Select.displayName = "Select";
Select.Option = Option;

// export Option
// There is not types defined for option :(
// Please refer here for now
// https://ant.design/components/select#option-props
// const { Option } = AntdSelect;
// export { Option };
